import * as React from "react";

import type { UseFormReturn } from "react-hook-form";

import { Text } from "@ps-design-system/components/Text";
import { useToast } from "@ps-design-system/components/Toast";

import { useIntl } from "react-intl-compat";

import {
  type FsvPricingErrorBody,
  useValidateFsvPricingMutation,
} from "../../../../components/src/services/listings.service";

import type { ListingFsvFormValues } from "./ListingFsvForm/ListingFsvForm.schema";
import {
  getFsvSaveError,
  isBlankAmount,
} from "./ListingFsvForm/ListingFsvForm.utils";
import { formatCurrencyAmount } from "./ListingFsvPage.utils";

type UseFsvPricingValidationArgs = {
  currency: string;
  form: UseFormReturn<ListingFsvFormValues>;
  listingId: string;
};

/** The error type the check sets, so it only clears its own errors. */
const PRICING_ERROR_TYPE = "pricing";

/** The error type a failed save sets; a passing check makes it stale too. */
const SAVE_ERROR_TYPE = "server";

const CLEARABLE_ERROR_TYPES = [PRICING_ERROR_TYPE, SAVE_ERROR_TYPE];

const PRICING_FIELDS = ["foodAndDrinkMinimum", "spaceRental"] as const;

const isAmount = (value: unknown) =>
  !isBlankAmount(value) &&
  Number.isInteger(Number(value)) &&
  Number(value) >= 0;

type PricingPair = { unitCost: number; flatRatePriceValue: number };

/** The pricing the check sends, or none while an amount is blank or invalid. */
const getPricingPair = (
  values: Partial<
    Pick<
      ListingFsvFormValues,
      "foodAndDrinkMinimum" | "hasSpaceRental" | "spaceRental"
    >
  > = {},
): PricingPair | undefined => {
  const { foodAndDrinkMinimum, hasSpaceRental, spaceRental } = values;
  if (
    !isAmount(foodAndDrinkMinimum) ||
    (hasSpaceRental && !isAmount(spaceRental))
  ) {
    return undefined;
  }

  return {
    unitCost: Number(foodAndDrinkMinimum),
    flatRatePriceValue: hasSpaceRental ? Number(spaceRental) : 0,
  };
};

const isSamePricing = (a?: PricingPair, b?: PricingPair) =>
  !!a &&
  !!b &&
  a.unitCost === b.unitCost &&
  a.flatRatePriceValue === b.flatRatePriceValue;

/**
 * Checks the F&B minimum and space rental by calling a new prefetch endpoint on input onBlur
 * The endpoint checks the Pay at venue tier schedule to ensure the space rental is within the tier's bounds
 * if a space rental is > 0 and under the floor minimum of the current food and drink minimum tier,
 * the space rental is automatically raised to the floor minimum. A toast is shown to the user to inform them of the change.
 */
const useFsvPricingValidation = ({
  currency,
  form,
  listingId,
}: UseFsvPricingValidationArgs) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [validateFsvPricing] = useValidateFsvPricingMutation();
  const inFlightRef = React.useRef<Promise<void>>();
  // The pricing last sent; until then, the listing's saved pricing counts.
  // `null` after a check that got no answer, so the next one always runs
  const lastCheckedRef = React.useRef<PricingPair | null>();
  const [isEditingPricing, setIsEditingPricing] = React.useState(false);
  const [isValidatingPricing, setIsValidatingPricing] = React.useState(false);

  const startEditingPricing = () => setIsEditingPricing(true);

  const clearPricingErrors = React.useCallback(() => {
    PRICING_FIELDS.forEach((field) => {
      const errorType = form.getFieldState(field).error?.type;
      if (errorType && CLEARABLE_ERROR_TYPES.includes(errorType)) {
        form.clearErrors(field);
      }
    });
  }, [form]);

  const validatePricing = React.useCallback(() => {
    setIsEditingPricing(false);

    // Blank or invalid amounts are the form's own validation to show
    const pricing = getPricingPair(form.getValues());
    if (!pricing) {
      return Promise.resolve();
    }

    // Nothing to check if the pricing hasn't changed since the last check
    const lastChecked =
      lastCheckedRef.current === undefined
        ? getPricingPair(form.formState.defaultValues)
        : lastCheckedRef.current;
    if (isSamePricing(pricing, lastChecked ?? undefined)) {
      return inFlightRef.current ?? Promise.resolve();
    }

    const { unitCost, flatRatePriceValue } = pricing;
    lastCheckedRef.current = pricing;
    setIsValidatingPricing(true);
    const request = validateFsvPricing({
      listingId,
      payload: {
        flat_rate_price_value: flatRatePriceValue,
        included_addon: { unit_cost: unitCost },
      },
    })
      .unwrap()
      .then(
        () => undefined,
        // Invalid pricing comes back as a 400 with the error body; a failure
        // without one (e.g. network) still counts as not valid
        (error: { data?: FsvPricingErrorBody }): FsvPricingErrorBody =>
          error?.data ?? {},
      )
      .then((pricingError) => {
        // Ignore a stale response if the host changed the pricing since.
        // If no newer check has started, forget this pricing too, so going
        // back to it checks again instead of skipping an unapplied result
        if (!isSamePricing(getPricingPair(form.getValues()), pricing)) {
          if (inFlightRef.current === request) {
            lastCheckedRef.current = null;
          }
          return;
        }

        clearPricingErrors();
        if (!pricingError) {
          return;
        }

        const minimum = pricingError.space_rental_amount_min;
        if (pricingError.error_code === "flat_rate_below_floor" && minimum) {
          form.setValue("hasSpaceRental", true, { shouldDirty: true });
          form.setValue("spaceRental", minimum, { shouldDirty: true });
          toast({
            title: "",
            description: (
              <Text typeface="body-1-m" element="p" className="tw-mb-0">
                {intl.formatMessage(
                  {
                    id: "hosting.listing_fsv.space_rental_raised_toast",
                    defaultMessage:
                      "Space rental raised to the {amount} minimum.",
                  },
                  { amount: formatCurrencyAmount(intl, minimum, currency) },
                )}
              </Text>
            ),
            variant: "black",
            duration: 3000,
          });
          return;
        }

        // Anything without a field (e.g. `fees_error`) doesn't block; the
        // save validates the pricing again and shows its error
        const { amount, field, message } = getFsvSaveError(pricingError);
        if (!field) {
          lastCheckedRef.current = null;
          return;
        }

        form.setError(field, {
          type: PRICING_ERROR_TYPE,
          message: intl.formatMessage(
            message,
            amount != null
              ? { amount: formatCurrencyAmount(intl, amount, currency) }
              : undefined,
          ),
        });
      })
      .finally(() => {
        if (inFlightRef.current === request) {
          inFlightRef.current = undefined;
          setIsValidatingPricing(false);
        }
      });

    inFlightRef.current = request;
    return request;
  }, [
    clearPricingErrors,
    currency,
    form,
    intl,
    listingId,
    toast,
    validateFsvPricing,
  ]);

  /**
   * Waits for an in-flight check, or runs one, before a save reads values.
   * Resolves to whether the pricing can be saved: false while a pricing field
   * shows a check or save error. A check that couldn't run doesn't block.
   */
  const ensurePricingValidated = React.useCallback(async () => {
    await (inFlightRef.current ?? validatePricing());

    return PRICING_FIELDS.every((field) => {
      const errorType = form.getFieldState(field).error?.type;
      return !errorType || !CLEARABLE_ERROR_TYPES.includes(errorType);
    });
  }, [form, validatePricing]);

  return {
    ensurePricingValidated,
    isEditingPricing,
    isValidatingPricing,
    startEditingPricing,
    validatePricing,
  };
};

export { SAVE_ERROR_TYPE, useFsvPricingValidation };
